import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import MultiSelectWithCheckbox from "../filter/MultiSelectWithCheckbox";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import moment from "moment";
import { Toast } from "../../common/toast/toast";

const ChartHeader = ({ title, onChange }) => {
  const viewOptions = [
    { value: "today", label: "Today" },
    { value: "lastweek", label: "Last Week" },
    { value: "thisweek", label: "This Week" },
    { value: "lastmonth", label: "Last Month" },
    { value: "thismonth", label: "This Month" },
    { value: "thisyear", label: "This Year" },
    { value: "custom", label: "Custom" },
  ];

  const mainRef = useRef(null);
  const dateFilterRef = useRef(null);
  const filterOpenRef = useRef(null);

  const trialNamesList = useSelector((state) => state.trails_name.data || []);
  const clinic_infoList = useSelector((state) => state.clinic_info.data || []);

  const [toasts, setToasts] = useState([]);
  const [dateFilter, setDateFilter] = useState("thisyear");
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const closePopup = () => {
    setDatePickerOpen(false);
    setIsDateFilterOpen(false);
  };

  const setDate = (type, val) => {
    setDateRange((prev) => ({ ...prev, [type]: val }));
  };

  const getDateRange = (option) => {
    const now = moment.utc(); // 👈 start from UTC, not local time

    switch (option) {
      case "today":
        return {
          startDate: now.clone().startOf("day").toDate(),
          endDate: now.clone().endOf("day").toDate(),
        };

      case "lastweek":
        return {
          startDate: now
            .clone()
            .subtract(1, "week")
            .startOf("isoWeek")
            .toDate(),
          endDate: now.clone().subtract(1, "week").endOf("isoWeek").toDate(),
        };

      case "thisweek":
        return {
          startDate: now.clone().startOf("isoWeek").toDate(),
          endDate: now.clone().endOf("isoWeek").toDate(),
        };

      case "lastmonth":
        return {
          startDate: now.clone().subtract(1, "month").startOf("month").toDate(),
          endDate: now.clone().subtract(1, "month").endOf("month").toDate(),
        };

      case "thismonth":
        return {
          startDate: now.clone().startOf("month").toDate(),
          endDate: now.clone().endOf("month").toDate(),
        };

      case "thisyear":
        return {
          startDate: now.clone().startOf("year").toDate(),
          endDate: now.clone().endOf("year").toDate(),
        };

      case "custom":
        return null;

      default:
        return null;
    }
  };

  const addToast = (message, type) => {
    const id = Date.now();
    setToasts([...toasts, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000); // Auto-dismiss after 3 seconds
  };

  const removeToast = (id) => {
    setToasts(toasts.filter((toast) => toast.id !== id));
  };

  const applyFilter = (date_Range) => {
    onChange({
      dateFilter,
      selectedLocations,
      selectedStudies,
      dateRange: date_Range || dateRange,
    });
    setIsFilterOpen(false);
    setIsDateFilterOpen(false);
    setDatePickerOpen(false);
  };

  const clearFilters = (date_Range) => {
    setSelectedLocations([]);
    setSelectedStudies([]);

    onChange({
      dateFilter,
      selectedLocations: [],
      selectedStudies: [],
      dateRange: date_Range || dateRange,
    });
  };

  const applyPopupDateFilter = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      addToast("Start date and  End date required", "error");
      return;
    }

    if (dateRange.startDate && dateRange.endDate) {
      if (
        moment.utc(dateRange.startDate).isAfter(moment.utc(dateRange.endDate))
      ) {
        addToast("Start date cannot be after end date", "error");
        return;
      }
    }

    applyFilter({
      startDate: moment(dateRange.startDate)
        .startOf("day")
        .format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
      endDate: moment(dateRange.endDate)
        .endOf("day")
        .format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
    });

    setDateFilter("custom");
    setDatePickerOpen(false);
    setIsDateFilterOpen(false);
  };

  useEffect(() => {
    if (dateFilter !== "custom") {
      const range = getDateRange(dateFilter);
      if (range) {
        // Convert to ISO strings explicitly in UTC
        const start = moment.utc(range.startDate).format();
        const end = moment.utc(range.endDate).format();

        setDateRange({ startDate: start, endDate: end });

        applyFilter({ startDate: start, endDate: end });
      }
    }
  }, [dateFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target && event.target.parentElement === null) {
        return;
      }

      if (event.target.closest(".multi-react-select__multi-value__remove")) {
        return;
      }

      if (event.target.closest(".multi-react-select__menu")) {
        return;
      }

      if (event.target.closest(".multi-react-select__control")) {
        return;
      }

      if (
        mainRef.current &&
        !mainRef.current.contains(event.target) &&
        dateFilterRef.current &&
        !dateFilterRef.current.contains(event.target)
      ) {
        setIsDateFilterOpen(false);
      }

      if (
        mainRef.current &&
        !mainRef.current.contains(event.target) &&
        filterOpenRef.current &&
        !filterOpenRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isFilterOpen, isDateFilterOpen, dateFilter]);

  return (
    <>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
      <div ref={mainRef} className="flex items-center gap-4 mb-4 ">
        <span className="font-semibold flex-1 text-[20px]">{title}</span>

        <div ref={dateFilterRef} className="w-48 z-[1]">
          <div className="relative w-full h-[42px]">
            <button
              onClick={() => setIsDateFilterOpen(!isDateFilterOpen)}
              className={`text-sm rounded-md ${isDateFilterOpen ? "rounded-b-none border-b-0 shadow-md" : ""} w-full p-2 flex items-center justify-between border border-blue-100`}
            >
              <span className="flex items-center px-2  gap-2 text-[16px]">
                {viewOptions.find((x) => x.value === dateFilter).label}
              </span>
              <span className="pointer-events-none px-1">
                <svg
                  width="18"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.9201 8.94995L13.4001 15.47C12.6301 16.24 11.3701 16.24 10.6001 15.47L4.08008 8.94995"
                    stroke="#245CEA"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            {isDateFilterOpen && (
              <>
                <div class="px-3 bg-white border-l border-r border-gray-200">
                  <div className="flex-1 border-t border-gray-30" />
                </div>
                <ul
                  className={`w-full bg-white border border-gray-200 shadow-md rounded-md ${isDateFilterOpen ? "rounded-t-none border-t-0" : ""} z-100`}
                >
                  {viewOptions.map((option) => (
                    <li
                      key={option.value}
                      onClick={() => {
                        if (option.value === "custom") {
                          setDatePickerOpen(true);
                          setDateRange({ startDate: null, endDate: null });
                          setIsDateFilterOpen(false);
                        } else {
                          setDateFilter(option.value);
                          setIsDateFilterOpen(false);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-100 z-100"
                    >
                      <input
                        type="radio"
                        checked={option.value === dateFilter}
                        readOnly
                      />
                      {option.label}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="relative flex text-left">
          <button onClick={() => setIsFilterOpen(!isFilterOpen)}>
            <svg
              width="4"
              height="20"
              viewBox="0 0 4 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="4" height="4" rx="1" fill="#1A1A1B" />
              <rect y="8" width="4" height="4" rx="1" fill="#1A1A1B" />
              <rect y="16" width="4" height="4" rx="1" fill="#1A1A1B" />
            </svg>
          </button>

          <div ref={filterOpenRef}>
            {isFilterOpen && (
              <div className="absolute top-[22px] right-[-10px] mt-2 w-[400px] bg-white border border-gray-200 rounded-lg shadow-lg  z-[99]">
                <div className="p-4 flex flex-col gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex-1">Locations</span>
                      <button
                        onClick={() =>
                          clearFilters({
                            startDate: moment(dateRange.startDate)
                              .startOf("day")
                              .format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
                            endDate: moment(dateRange.endDate)
                              .endOf("day")
                              .format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
                          })
                        }
                        className="text-sm text-gray-500 hover:text-red-500"
                      >
                        X Clear
                      </button>
                    </div>
                    <MultiSelectWithCheckbox
                      key={selectedLocations.join(",")}
                      selectedValues={
                        clinic_infoList
                          ?.filter((t) =>
                            selectedLocations.includes(t.clinicalInfoID),
                          ) // only selected IDs
                          .map((t) => ({
                            value: t.clinicalInfoID,
                            label: t.name,
                          })) || []
                      }
                      placeholder="Search and Select Locations..."
                      options={
                        clinic_infoList?.map((t) => ({
                          value: t.clinicalInfoID,
                          label: t.name,
                        })) || []
                      }
                      onChange={(val) => {
                        setSelectedLocations(val.selectedIds); // still store just the IDs in state
                      }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span>Studies</span>
                    </div>
                    <MultiSelectWithCheckbox
                      key={selectedStudies.join(",")}
                      selectedValues={
                        trialNamesList
                          ?.filter((t) => selectedStudies.includes(t.trialID)) // keep only selected IDs
                          .map((t) => ({
                            value: t.trialID,
                            label: t.trialName,
                          })) || []
                      }
                      placeholder="Search and Select Studies..."
                      options={
                        trialNamesList.map((t) => ({
                          value: t.trialID,
                          label: t.trialName,
                        })) || []
                      }
                      onChange={(val) => {
                        setSelectedStudies(val.selectedIds);
                      }}
                    />
                  </div>

                  <button
                    onClick={() => applyFilter()}
                    className="flex items-center justify-center text-center 
                             btn-primary-base-bar primary_btn 
                             text-white py-2 px-5 rounded-[5px] 
                             hover:bg-white border hover:border-[#245CEA] 
                             hover:text-[#1A1A1B] transition duration-300 
                             h-[42px] text-[14px] font-mont font-[500]"
                  >
                    Filter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {datePickerOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <div>
              <div>
                <label for="timezone" class="block form_label_color pb-4">
                  Start Date
                </label>
                <div className="flex text-sm items-center relative">
                  <input
                    type="date"
                    id="startDate"
                    value={
                      dateRange.startDate
                        ? moment.utc(dateRange.startDate).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      setDate("startDate", e.target.value);
                    }}
                    className="border rounded-lg block w-full p-2.5 h-12  mb-3"
                  />
                </div>
              </div>
              <div>
                <label for="timezone" class="block form_label_color pb-4">
                  End Date
                </label>
                <div className="flex text-sm items-center relative">
                  <input
                    type="date"
                    id="endDate"
                    min={dateRange.startDate || undefined}
                    disabled={!dateRange.startDate}
                    value={dateRange.endDate || ""}
                    onChange={(e) => {
                      setDate("endDate", e.target.value);
                    }}
                    className="border rounded-lg block w-full p-2.5 h-12  mb-3"
                  />
                </div>
              </div>

              <div className="flex flex-row justify-end gap-5 mt-5">
                <button
                  className="px-5 py-1 text-base rounded-md border primary_btn transition-all duration-500"
                  onClick={() => {
                    applyPopupDateFilter();
                  }}
                >
                  Apply
                </button>
                <SecondaryButton
                  onClick={closePopup}
                  label="Cancel"
                  classes={"px-5 py-3"}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChartHeader;
